/*
 * Lives in @bandway/ui-primitives — desktop + web SPA share this primitive.
 *
 * Vendored / adapted from wavesurfer.js v7.10.1.
 *
 * Original source:
 *   https://github.com/katspaugh/wavesurfer.js/blob/v7.10.1/src/renderer.ts
 *
 * Original copyright:
 *   Copyright (c) 2012-2023, katspaugh and contributors. BSD-3-Clause.
 *   Full license text: see THIRD_PARTY_NOTICES.md in repository root.
 *
 * Modifications for Band Way:
 *   - Output is an SVG string instead of a canvas draw.
 *   - Consumes our `{minPeaks, maxPeaks}` shape directly.
 *   - Drops barAlign / progress / responsive logic — single full-width render.
 *   - Trimmed normalization to a pure-function helper.
 */

const DEFAULTS = {
  barWidth:  2,
  barGap:    1,
  barRadius: 1,
  color:     '#888',
};

/**
 * Render an asymmetric min/max ribbon as bar rects.
 *
 * @param {{minPeaks: Float32Array, maxPeaks: Float32Array}} peaks
 * @param {{width: number, height: number, barWidth?: number, barGap?: number,
 *          barRadius?: number, color?: string}} opts
 * @returns {string} `<svg>...</svg>` string (or '' for empty input).
 */
export function drawBars(peaks, opts) {
  if (!peaks?.minPeaks?.length || !peaks?.maxPeaks?.length) return '';
  const w     = opts.width,  h = opts.height;
  const bw    = opts.barWidth  ?? DEFAULTS.barWidth;
  const bg    = opts.barGap    ?? DEFAULTS.barGap;
  const br    = opts.barRadius ?? DEFAULTS.barRadius;
  const color = opts.color     ?? DEFAULTS.color;
  const stride = bw + bg;
  const numBars = Math.floor(w / stride);
  if (numBars <= 0) return '';

  const cy  = h / 2;
  const amp = h / 2;
  // Group-max downsampling: each output bar reduces its proportional slice
  // of source peaks to (max of maxPeaks, min of minPeaks). Replaces the
  // previous slice-by-numBars which silently dropped tail peaks when
  // srcLen > numBars (spec 2026-05-12-waveform-bar-style-amber-design).
  const srcLen = Math.min(peaks.minPeaks.length, peaks.maxPeaks.length);
  const rects = [];
  for (let i = 0; i < numBars; i++) {
    const srcStart = Math.floor((i     * srcLen) / numBars);
    const srcEnd   = Math.min(srcLen, Math.floor(((i + 1) * srcLen) / numBars));
    if (srcEnd <= srcStart) continue;
    let maxV = peaks.maxPeaks[srcStart];
    let minV = peaks.minPeaks[srcStart];
    for (let j = srcStart + 1; j < srcEnd; j++) {
      const mv = peaks.maxPeaks[j];
      const nv = peaks.minPeaks[j];
      if (mv > maxV) maxV = mv;
      if (nv < minV) minV = nv;
    }
    const x      = i * stride;
    const yTop   = Math.round(cy - maxV * amp);
    const yBot   = Math.round(cy - minV * amp);
    const height = Math.max(1, yBot - yTop);
    rects.push(
      `<rect x="${x}" y="${yTop}" width="${bw}" height="${height}" rx="${br}" fill="${color}"/>`
    );
  }
  return `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" width="100%" height="100%">${rects.join('')}</svg>`;
}

/**
 * Scale `{minPeaks, maxPeaks}` so the largest absolute extreme equals ±`target`.
 *
 * Two modes:
 *   - `target=1` (default): canonical normalization, peak hits ±1. Used when
 *     a quiet recording must render at full height (e.g. previous overview
 *     auto-scale toggle).
 *   - `target < 1`: visual-headroom mode — scale ONLY DOWN when max abs >
 *     target, leave quiet content untouched. Use this to keep loud per-track
 *     waveforms from saturating the row height, AND to fit a summed overview
 *     (where peaks can exceed 1.0) back into the SVG bounds. Relative
 *     loudness between bins is preserved.
 *
 * @param {{minPeaks: Float32Array, maxPeaks: Float32Array}} peaks
 * @param {number} [target=1] — desired peak in [0, 1]
 * @returns {{minPeaks: Float32Array, maxPeaks: Float32Array}}
 */
export function normalizePeaks(peaks, target = 1) {
  const mn = peaks.minPeaks, mx = peaks.maxPeaks;
  let maxAbs = 0;
  for (let i = 0; i < mn.length; i++) {
    const a = Math.abs(mn[i]); if (a > maxAbs) maxAbs = a;
    const b = Math.abs(mx[i]); if (b > maxAbs) maxAbs = b;
  }
  if (maxAbs === 0) return peaks;
  // target=1 → canonical normalize (always scale to ±1).
  // target<1 → headroom-fit: leave quiet content alone, only shrink loud.
  if (target >= 1 ? maxAbs === 1 : maxAbs <= target) return peaks;
  const k = target / maxAbs;
  const minOut = new Float32Array(mn.length);
  const maxOut = new Float32Array(mx.length);
  for (let i = 0; i < mn.length; i++) {
    minOut[i] = mn[i] * k;
    maxOut[i] = mx[i] * k;
  }
  return { minPeaks: minOut, maxPeaks: maxOut };
}

/**
 * Draw symmetric ribbon waveform — mirrored above/below center line.
 * Returns an `<svg>` string suitable for `innerHTML` insertion.
 *
 * Used by web SPA for per-track waveforms in Phase E.
 *
 * @param {Float32Array|number[]} peaks  Normalized peaks 0..1
 * @param {object} opts
 * @param {number} opts.width  px
 * @param {number} opts.height px
 * @param {string} [opts.fillStyle='currentColor']
 * @param {number} [opts.barWidth=2]
 * @param {number} [opts.barGap=1]
 * @returns {string}
 */
export function drawSymRibbon(peaks, opts) {
  const { width, height, fillStyle = 'currentColor', barWidth = 2, barGap = 1 } = opts;
  const stride = barWidth + barGap;
  const barCount = Math.floor(width / stride);
  const center = height / 2;
  const parts = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none">`];
  for (let i = 0; i < barCount; i++) {
    const idx = Math.floor((i / barCount) * peaks.length);
    const v = Math.abs(peaks[idx] || 0);
    const halfH = v * center;
    const x = i * stride;
    const y = center - halfH;
    const h = halfH * 2;
    parts.push(`<rect x="${x}" y="${y}" width="${barWidth}" height="${h}" fill="${fillStyle}"/>`);
  }
  parts.push('</svg>');
  return parts.join('');
}
